import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ChartHeader from 'components/charts/ChartHeader';
import CompositeFilter, { ANALYSIS_FILTERS, getActiveFilters } from 'components/charts/CompositeFilter';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import Legend from 'components/charts/chartJS/CustomChartLegend';
import { FiltersContainer } from 'components/charts/styled/Chart.styled';
import Button from 'components/field/Button';
import Card from 'components/global/Card';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';

import useBrandManagerFilter from 'hooks/useBrandManagerFilter';
import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { REPORT } from 'services/consts';

import { colorCombinations } from 'utils/colors';

import OverviewCharts, { DeliveryAging, PickupAging } from './OverviewCharts';
import * as S from './OverviewCharts.styled';
import PendingShipments from './PendingShipments';

const Overview = () => {
	const { userType, assignedBrands } = useSelector((state) => state.user);
	const isBrandManager = userType === 'BrandManager';

	return (
		<WidgetPageContainer>
			<WidgetWrapper widgetId={'analysis.overview'}>
				<OverviewCharts isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'analysis.pickupAging'}>
				<PickupAging isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'analysis.deliveryAging'}>
				<DeliveryAging isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'analysis.totalPendingByDC'}>
				<TotalPendingByDC isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'analysis.pendingShipments'}>
				<PendingShipments isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
			</WidgetWrapper>
		</WidgetPageContainer>
	);
};

export const TotalPendingByDC = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const filtersList = [
		ANALYSIS_FILTERS.delayType,
		ANALYSIS_FILTERS.shipmentCreatedDate,
		ANALYSIS_FILTERS.originCountry,
		ANALYSIS_FILTERS.originCity,
		ANALYSIS_FILTERS.brand,
		ANALYSIS_FILTERS.destinationCountry,
		ANALYSIS_FILTERS.destinationCity,
		ANALYSIS_FILTERS.deliveryCompanyName,
		ANALYSIS_FILTERS.shipmentFlow,
		ANALYSIS_FILTERS.serviceType
	];

	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(() => ({ filter: getActiveFilters(extraFilters) }), [extraFilters]);

	const parseData = ({ data }) => {
		const labels = data?.map((item) => item.deliveryCompanyName ?? '-');
		const datasets = ['greenCount', 'orangeCount', 'redCount'].map((key, idx) => ({
			data: data?.map((item) => item[key]),
			backgroundColor: colorCombinations[15 - idx].color,
			borderColor: colorCombinations[15 - idx].color
		}));
		return { labels, datasets };
	};

	const skipNextApply = useRef(false);

	useEffect(() => {
		if (defaultFilters) {
			if (Array.isArray(defaultFilters.filter) && defaultFilters.filter.length > 0) {
				setExtraFilters((prevFilters) =>
					prevFilters.map((filterItem) => {
						const saved = defaultFilters.filter.find((f) => {
							return f.key[0] === filterItem.filterValues.key[0];
						});
						return saved ? { ...filterItem, filterValues: saved } : filterItem;
					})
				);
			}
			skipNextApply.current = true;
		}
	}, [defaultFilters]);

	useEffect(() => {
		if (onApplyFilters) {
			if (skipNextApply.current) {
				skipNextApply.current = false;
				return;
			}
			onApplyFilters(body);
		}
	}, [body]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DELIVERY_COMPANY.GET_AGGREGATOR_AGING_DC_ANALYSIS_CLIENT,
		body,
		parseData
	});

	return (
		<Card ref={ref}>
			<div>
				<ChartHeader
					title={widgetName || 'totalPendingByDC'}
					titleTooltip={'totalPendingByDCTooltip'}
					filter={
						<FiltersContainer>
							<Button
								id="filters-button"
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
								ref={setFilterMenuButtonRef}
								variant="borderless"
								label="filters"
								className="py-0"
							/>
							<CompositeFilter
								filtersMenuButtonRef={filtersMenuButtonRef}
								filtersList={extraFilters}
								setFiltersList={setExtraFilters}
							/>
						</FiltersContainer>
					}
				/>
				<BarChartV2
					data={data}
					loading={loading}
					xTitle={'deliveryCompanies'}
					yTitle={'orders'}
					aspectRatio={3}
				/>
				<S.LegendWrapper>
					{data && data.labels.length > 0 && (
						<Legend
							showAmount={false}
							data={{ labels: ['low', 'medium', 'high'] }}
							customColors={[
								colorCombinations[colorCombinations.length - 1],
								colorCombinations[colorCombinations.length - 2],
								colorCombinations[colorCombinations.length - 3]
							]}
							placeItems="horizontal"
						/>
					)}
				</S.LegendWrapper>
			</div>
		</Card>
	);
};

export default Overview;
